import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Mail, Clock, Linkedin, Phone, ListChecks, Eye, UserPlus, MessageCircle } from "lucide-react";

const CHANNEL_META = {
  email: { icon: Mail, label: "Email", color: "text-primary" },
  linkedin_view: { icon: Eye, label: "LinkedIn view", color: "text-[#0a66c2]" },
  linkedin_connect: { icon: UserPlus, label: "LinkedIn connect", color: "text-[#0a66c2]" },
  linkedin_dm: { icon: MessageCircle, label: "LinkedIn DM", color: "text-[#0a66c2]" },
  voicemail: { icon: Phone, label: "Voicemail", color: "text-foreground" },
  task_manual: { icon: ListChecks, label: "Manual task", color: "text-foreground" },
};

export default function Sequences() {
  const [seqs, setSeqs] = useState([]);

  useEffect(() => {
    api.get("/sequences").then((r) => setSeqs(r.data)).catch(() => {});
  }, []);

  return (
    <Layout title="Sequences" subtitle="Multi-channel cadences">
      {seqs.map((s) => (
        <div key={s.sequence_id} className="bg-card border border-border p-6 mb-6" style={{ borderRadius: 2 }} data-testid={`sequence-${s.sequence_id}`}>
          <div className="flex items-start justify-between mb-1">
            <div>
              <div className="font-serif text-2xl tracking-tight" style={{ fontWeight: 400 }}>{s.name}</div>
              <div className="text-sm text-muted-foreground mt-1">{s.description}</div>
            </div>
            <div className="overline">{s.active ? "Active" : "Paused"}</div>
          </div>

          <div className="mt-3 flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
            <span>{s.steps.length} steps</span>
            {Object.keys(CHANNEL_META).map((ch) => {
              const count = s.steps.filter((st) => (st.channel || "email") === ch).length;
              if (!count) return null;
              const M = CHANNEL_META[ch];
              return (
                <span key={ch} className="inline-flex items-center gap-1 font-mono" data-testid={`seq-summary-${ch}`}>
                  · <M.icon className="h-3 w-3" strokeWidth={1.5} /> {count} {M.label.toLowerCase()}
                </span>
              );
            })}
          </div>

          <div className="mt-8 space-y-4">
            {s.steps.map((step, idx) => {
              const ch = step.channel || "email";
              const M = CHANNEL_META[ch] || CHANNEL_META.email;
              const Icon = M.icon;
              const isManual = ch !== "email";
              return (
                <div key={step.step_id || step.order} className="grid grid-cols-12 gap-4 items-start fade-up">
                  <div className="col-span-1 flex flex-col items-center pt-1">
                    <div className={`h-7 w-7 flex items-center justify-center text-xs font-mono ${isManual ? "bg-secondary text-foreground border border-border" : "bg-foreground text-background"}`} style={{ borderRadius: 2 }}>{step.order}</div>
                    {idx < s.steps.length - 1 && <div className="w-px h-16 bg-border mt-2" />}
                  </div>
                  <div className={`col-span-11 border p-4 ${isManual ? "bg-secondary/30 border-border" : "bg-background border-border"}`} style={{ borderRadius: 2 }} data-testid={`seq-step-${step.order}`}>
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <Icon className={`h-3.5 w-3.5 ${M.color}`} strokeWidth={1.5} />
                      <div className="overline">{M.label}</div>
                      {ch === "email" && <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">{(step.kind || "").replace(/_/g, " ")}</div>}
                      {isManual && <div className="text-[10px] uppercase tracking-widest text-[hsl(var(--warning))] font-mono">manual task</div>}
                      <div className="text-xs text-muted-foreground inline-flex items-center gap-1 ml-auto">
                        <Clock className="h-3 w-3" strokeWidth={1.5} /> {idx === 0 ? "Day 0" : `Wait ${step.delay_days}d`}
                      </div>
                    </div>
                    {ch === "email" ? (
                      <>
                        <div className="font-medium">{step.subject_template}</div>
                        <div className="text-xs text-muted-foreground mt-1">Body: AI-generated per lead using research + trigger context.</div>
                      </>
                    ) : (
                      <div className="text-sm">{step.task_note || "Manual task — see lead context."}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-xs text-muted-foreground">
            Multi-channel cadence: email auto-sends · LinkedIn / voicemail steps create open tasks that surface in the <strong className="text-foreground">Sales Coach</strong> on the Dashboard for one-click completion.
          </div>
        </div>
      ))}
    </Layout>
  );
}
